import { canvasUnitsPerPixel } from '@/utils/canvasUnits';
import type { CanvasEx } from '@/typings/canvas';
import type { BaseCanvasElement, CanvasElement } from '@/typings/customilyElements/canvasElements';
import type { Object as FabricObject } from 'fabric/fabric-impl';

export const SELECTION_TOOLBAR_ID = 'fabric-selection-toolbar';

export const enum SELECTION_TOOLBAR_EVENT {
    SHOW = 'selectiontoolbar:show',
    HIDE = 'selectiontoolbar:hide'
}

export const enum SELECTION_TOOLBAR_ACTION {
    DELETE = 'deleteSelection',
    FIT_TO_CANVAS = 'fitToCanvas',
    ALIGN_TOP = 'alignTop',
    ALIGN_MIDDLE = 'alignMiddle',
    ALIGN_BOTTOM = 'alignBottom',
    ALIGN_LEFT = 'alignLeft',
    ALIGN_CENTER = 'alignCenter',
    ALIGN_RIGHT = 'alignRight'
}

export interface SelectionToolbarItem {
    title?: string;
    icon?: [string, string];
    hidden?: boolean;
    onClick?: () => void | Promise<void>;
    action?: SELECTION_TOOLBAR_ACTION;
    isDivider?: boolean;
}

export interface SelectionToolbar {
    /** Viewport coordinates of the selection's bottom edge center */
    x: number;
    y: number;
    selectedElement?: FabricObject;
    items: SelectionToolbarItem[];
}

export class FabricSelectionToolbar {
    private lastX: number | null = null;
    private lastY: number | null = null;
    private suspended = false;

    constructor(public canvas: CanvasEx) {
        this.showToolbar = this.showToolbar.bind(this);
        this.hideToolbar = this.hideToolbar.bind(this);
        this.suspend = this.suspend.bind(this);
        this.resume = this.resume.bind(this);
        this.reposition = this.reposition.bind(this);
    }

    private canCopyPaste(): boolean {
        const { enableCopyPaste } = this.canvas;
        return typeof enableCopyPaste === 'function'
            ? enableCopyPaste(this.canvas.getActiveObjects())
            : !!enableCopyPaste;
    }

    private canDelete(): boolean {
        const { canDeleteObject } = this.canvas;
        if (!canDeleteObject) return true;

        const elements = this.canvas.getActiveObjects() || [];
        return elements.every((element) => element && canDeleteObject.call(this.canvas, element as CanvasElement));
    }

    private getToolbarItems(): SelectionToolbarItem[] {
        return [
            {
                title: 'Duplicate',
                icon: ['far', 'clone-plus'],
                hidden: !this.canCopyPaste(),
                onClick: () => this.canvas._cloneSelection()
            },
            {
                title: 'Delete',
                icon: ['far', 'trash-can'],
                hidden: !this.canDelete(),
                action: SELECTION_TOOLBAR_ACTION.DELETE
            },
            { isDivider: true },
            {
                title: 'Fit to Canvas',
                icon: ['far', 'expand'],
                hidden: this.canvas.getActiveObject()?.isType('activeSelection'),
                action: SELECTION_TOOLBAR_ACTION.FIT_TO_CANVAS
            },
            { isDivider: true },
            {
                title: 'Align Top',
                icon: ['far', 'objects-align-top'],
                action: SELECTION_TOOLBAR_ACTION.ALIGN_TOP
            },
            {
                title: 'Align to Center',
                icon: ['far', 'objects-align-center-vertical'],
                action: SELECTION_TOOLBAR_ACTION.ALIGN_CENTER
            },
            {
                title: 'Align to Bottom',
                icon: ['far', 'objects-align-bottom'],
                action: SELECTION_TOOLBAR_ACTION.ALIGN_BOTTOM
            },
            {
                title: 'Align to Left',
                icon: ['far', 'objects-align-left'],
                action: SELECTION_TOOLBAR_ACTION.ALIGN_LEFT
            },
            {
                title: 'Align to Middle',
                icon: ['far', 'objects-align-center-horizontal'],
                action: SELECTION_TOOLBAR_ACTION.ALIGN_MIDDLE
            },
            {
                title: 'Align to Right',
                icon: ['far', 'objects-align-right'],
                action: SELECTION_TOOLBAR_ACTION.ALIGN_RIGHT
            }
        ];
    }

    /**
     * Anchor point in viewport coordinates: horizontal center of the selection's bounding box, at
     *  its bottom edge.
     */
    private getAnchor(element: FabricObject): { x: number; y: number } | null {
        const canvasEl = this.canvas.upperCanvasEl;
        if (!canvasEl) return null;

        // No arguments on purpose: passing calculate=true routes through calcOCoords, which returns
        //  the *control* positions, so the rotation chip's offsetY would stretch the box upwards
        const box = element.getBoundingRect();
        const unitsPerPixel = canvasUnitsPerPixel(this.canvas);
        const canvasRect = canvasEl.getBoundingClientRect();

        return {
            x: canvasRect.left + (box.left + box.width / 2) / unitsPerPixel,
            y: canvasRect.top + (box.top + box.height) / unitsPerPixel
        };
    }

    private showToolbar() {
        const element = this.canvas.getActiveObject();
        if (!element || this.suspended || (element as BaseCanvasElement).locked || !element.visible) return;

        const anchor = this.getAnchor(element);
        if (!anchor) return;

        this.lastX = anchor.x;
        this.lastY = anchor.y;
        this.canvas.fire(SELECTION_TOOLBAR_EVENT.SHOW, {
            ...anchor,
            selectedElement: element,
            items: this.getToolbarItems()
        } as SelectionToolbar);
    }

    private hideToolbar() {
        this.lastX = null;
        this.lastY = null;
        this.canvas.fire(SELECTION_TOOLBAR_EVENT.HIDE);
    }

    private suspend() {
        this.suspended = true;
        this.canvas.fire(SELECTION_TOOLBAR_EVENT.HIDE);
    }

    private resume() {
        this.suspended = false;
        this.showToolbar();
    }

    /**
     * CanvasEx fires no zoom/pan event, so after:render is the only hook covering moving, scaling,
     *  zooming, panning and workspace resizes alike. It runs every frame, hence the change guard:
     *  without it every render would push a new value into the Vue layer.
     */
    private reposition() {
        const element = this.canvas.getActiveObject();
        if (!element || this.suspended) return;

        const anchor = this.getAnchor(element);
        if (!anchor || (anchor.x === this.lastX && anchor.y === this.lastY)) return;

        this.showToolbar();
    }

    init() {
        this.canvas.on('selection:created', this.showToolbar);
        this.canvas.on('selection:updated', this.showToolbar);
        this.canvas.on('selection:cleared', this.hideToolbar);
        // Hidden while dragging so it stops chasing the pointer and colliding with the angle badge
        this.canvas.on('object:moving', this.suspend);
        this.canvas.on('object:scaling', this.suspend);
        this.canvas.on('object:rotating', this.suspend);
        this.canvas.on('mouse:up', this.resume);
        this.canvas.on('after:render', this.reposition);
    }

    destroy() {
        this.canvas.off('selection:created', this.showToolbar);
        this.canvas.off('selection:updated', this.showToolbar);
        this.canvas.off('selection:cleared', this.hideToolbar);
        this.canvas.off('object:moving', this.suspend);
        this.canvas.off('object:scaling', this.suspend);
        this.canvas.off('object:rotating', this.suspend);
        this.canvas.off('mouse:up', this.resume);
        this.canvas.off('after:render', this.reposition);
    }
}
